import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react'
import styles from './TwoLevelDropdown.module.sass'
import DropdownElement from '../DropdownElement/DropdownElement'
import Popover from '../../../etc/Popover'
import ShadowContainer from '../../../etc/popover/ShadowContainer/ShadowContainer'
import Field from '../../../controls/Field/Field'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import {Checkbox} from '../../../controls/Checkbox/Checkbox'
import clsx from 'clsx'
import { getId, idToggle} from '../../../../utils/entity-utils'
import {Classes, Colors} from '../../../../styles'
import {Entity, ID} from '../../../../types/UtilityTypes'
import Button from '../../../controls/Button/Button'
import ChevronDownIcon from 'files/chevron/down.svg'
import Spacer from '../../../etc/Spacer'
import Ellipsis from '../../../etc/Ellipsis/Ellipsis'


interface GeneralProps<A> {
    className?: string,
    placeholder?: string,
    data: A[],
    request: (query: {search: string, reset: boolean}) => void, // this function should be stabilized
}

interface PropsL1<A> {
    levels: 1,
    selection: [A[]],
    renders: [(item: A) => string],
    onChange: (data: {
        level: 0,
        item: A,
        selected: boolean,
    }) => void,
}

interface PropsL2<A, B> {
    levels: 2,
    selectors: [(item: A) => B[]]
    selection: [A[], B[]],
    renders: [(item: A) => string, (item: B) => string]
    onChange: (data: {
        level: 0,
        item: A,
        selected: boolean,
    } | {
        level: 1,
        item: B,
        selected: boolean,
    }) => void
}

interface PropsL3<A, B, C> {
    levels: 3,
    selectors: [(item: A) => B[], (item: B) => C[]]
    selection: [A[], B[], C[]],
    renders: [(item: A) => string, (item: B) => string, (item: C) => string]
    onChange: (data: {
        level: 0,
        item: A,
        selected: boolean,
    } | {
        level: 1,
        item: B,
        selected: boolean,
    } | {
        level: 2,
        item: C,
        selected: boolean,
    }) => void
}

type Props<A, B, C> = GeneralProps<A> & (PropsL1<A> | PropsL2<A, B> | PropsL3<A, B, C>)

const MultiLevelDropdown = <A extends Entity, B extends Entity = Entity, C extends Entity = Entity> ({request, ...props}: Props<A, B, C>): JSX.Element => {
    const [open, setOpen] = useState(false)
    const [opened, setOpened] = useState<ID[][]>([[], [], []])
    const [search, setSearch] = useState('')

    const selectionIds = useMemo(() => (props.selection as Entity[][]).map(selection => selection.map(getId)), [props.selection])
    const count = props.selection.flat(3).length

    useEffect(() => request({search, reset: true}), [search, request])
    const firstOpen = useRef(true)
    useEffect(() => {
        if (open && firstOpen.current) {
            request({search: '', reset: true})
            firstOpen.current = false
        }
    }, [open, request])

    return <Popover
        isOpen={open}
        onClickOutside={() => setOpen(false)}
        content={state => <ShadowContainer className={styles.dropdown} state={state}>
            <Field className={styles.field} value={search} onChange={setSearch}/>
            <InfiniteScroll className={styles.list} onReach={() => request({search, reset: false})}>
                {props.data.map(itemA => {
                    const open = opened[0].includes(itemA.id)

                    return <Fragment key={itemA.id}>
                        <Checkbox
                            className={styles.line}
                            color={Colors.grey}
                            value={selectionIds[0].includes(itemA.id)}
                            onChange={selected => props.onChange({
                                level: 0,
                                item: itemA,
                                selected,
                            })}
                            // intermediate={intersection(eqEntity)(props.selector(item), props.selection).length > 0}
                        >
                            <Ellipsis>{props.renders[0](itemA)}</Ellipsis>
                            <Spacer/>
                            {
                                props.levels > 1 && <Button onClick={event => {
                                    event.stopPropagation()
                                    setOpened(([level1, ...opened]) => [
                                        idToggle(itemA.id)(level1),
                                        ...opened,
                                    ])
                                }}>
                                    <ChevronDownIcon className={clsx({[Classes.flip]: open})}/>
                                </Button>
                            }
                        </Checkbox>
                        {
                            (props.levels === 2 || props.levels === 3) && open && props.selectors[0](itemA).map(itemB => {
                                const open = opened[1].includes(itemB.id)

                                return <Fragment key={itemB.id}>
                                    <Checkbox
                                        className={clsx(styles.line, styles.brand)}
                                        color={Colors.grey}
                                        value={selectionIds[1].includes(itemB.id)}
                                        onChange={selected => props.onChange({
                                            level: 1,
                                            item: itemB,
                                            selected,
                                        })}
                                    >
                                        <Ellipsis>{props.renders[1](itemB)}</Ellipsis>
                                        <Spacer/>
                                        {
                                            props.levels > 2 && <Button onClick={event => {
                                                event.stopPropagation()
                                                setOpened(([level1, level2, ...opened]) => [
                                                    level1,
                                                    idToggle(itemB.id)(level2),
                                                    ...opened,
                                                ])
                                            }}>
                                                <ChevronDownIcon className={clsx({[Classes.flip]: open})}/>
                                            </Button>
                                        }
                                    </Checkbox>
                                    {
                                        open && props.levels === 3 && props.selectors[1](itemB).map(itemC => <Checkbox
                                            key={itemC.id}
                                            className={clsx(styles.line, styles.brand)}
                                            color={Colors.grey}
                                            value={selectionIds[2].includes(itemC.id)}
                                            onChange={selected => props.onChange({
                                                level: 2,
                                                item: itemC,
                                                selected,
                                            })}
                                        >
                                            <Ellipsis>{props.renders[2](itemC)}</Ellipsis>
                                        </Checkbox>)
                                    }
                                </Fragment>
                            })
                        }
                    </Fragment>
                })}
            </InfiniteScroll>
        </ShadowContainer>}
    >
        <DropdownElement
            className={props.className}
            active={open}
            onToggle={setOpen}
            placeholder={props.placeholder}
        >
            {
                count > 0 && <>
                    <div className={styles.count}>{count}</div>
                    <Ellipsis className={styles.content}>
                        {([
                            ...props.levels === 1? props.selection[0].map(props.renders[0]): [],
                            ...props.levels === 2? props.selection[1].map(props.renders[1]): [],
                            ...props.levels === 3? props.selection[2].map(props.renders[2]): [],
                        ]).join()}
                    </Ellipsis>
                </>
            }
        </DropdownElement>
    </Popover>
}

export default MultiLevelDropdown



/*interface _Props<A, B> {
    className?: string,
    placeholder?: string,
    selection: A[],
    subselection: B[],
    selector: (item: A) => B[],
    render: (item: A) => string,
    subRender: (subItem: B) => string,
    request: (query: Query) => Observable<TypedAjaxResponse<ListResponse<A>>>
    onSelectionToggle?: () => void,
    onSelectionChange: (selection: A[]) => void,
    onSubselectionToggle?: () => void,
    onSubselectionChange: (subselection: B[]) => void,
}*/

/*const TwoLevelDropdown = <A extends Entity, B extends Entity>(props: Props<A, B>): JSX.Element => {
    const [opened, setOpened] = useState<ID[]>([])
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [data, getData] = useObservableState<A[], { search: string, reset: boolean }>(fromQueryWithReset(props.request), [])

    const count = props.selection.length + props.subselection.length
    const selectionIds = useMemo(() => props.selection.map(getId), [props.selection])
    const subselectionIds = useMemo(() => props.subselection.map(getId), [props.subselection])

    useEffect(() => getData({search, reset: true}), [getData, search])

    return <Popover
        isOpen={open}
        onClickOutside={() => setOpen(false)}
        content={state => <ShadowContainer className={styles.dropdown} state={state}>
            <Field className={styles.field} value={search} onChange={setSearch}/>
            <InfiniteScroll className={styles.list} onReach={() => getData({search, reset: false})}>
                {data.map(item => {
                    const open = opened.includes(item.id)
                    return <Fragment key={item.id}>
                        <Checkbox
                            className={styles.line}
                            color={Colors.grey}
                            value={selectionIds.includes(item.id)}
                            onChange={() => props.onSelectionChange(entityToggle(item)(props.selection))}
                            intermediate={intersection(eqEntity)(props.selector(item), props.selection).length > 0}
                        >
                            <Ellipsis>{props.render(item)}</Ellipsis>
                            <Spacer/>
                            <Button onClick={event => {
                                event.stopPropagation()
                                setOpened(idToggle(item.id))
                            }}>
                                <ChevronDownIcon className={clsx({[Classes.flip]: open})}/>
                            </Button>
                        </Checkbox>
                        {
                            open && props.selector(item).map(subItem => <Checkbox
                                key={subItem.id}
                                className={clsx(styles.line, styles.brand)}
                                color={Colors.grey}
                                value={subselectionIds.includes(subItem.id)}
                                onChange={() => props.onSubselectionChange(entityToggle(subItem)(props.subselection))}
                            >
                                {props.subRender(subItem)}
                            </Checkbox>)
                        }
                    </Fragment>
                })}
            </InfiniteScroll>
        </ShadowContainer>}
    >
        <DropdownElement
            className={props.className}
            active={open}
            onToggle={setOpen}
            placeholder={props.placeholder}
        >
            {
                count > 0 && <>
                    <div className={styles.count}>{count}</div>
                    <Ellipsis className={styles.content}>
                        {props.selection.map(props.render).concat(props.subselection.map(props.subRender)).join()}
                    </Ellipsis>
                </>
            }
        </DropdownElement>
    </Popover>
}*/
